/* Price display block.
 *
 * SOP Phase 1.5 — honest urgency only. The previous implementation
 * injected a deterministic 30-60% strikethrough on every product card
 * via `getAnchorPrice(slug)` — every product looked like it was on sale
 * forever. That's the perpetual-fake-sale pattern the SOP explicitly
 * forbids: "No 'was' price that was never charged. No countdown that
 * resets." This component now renders just the real price.
 *
 * Real promotional anchors (legitimate launch pricing on hero bundles,
 * niche bundles, the Insider Pass) live on dedicated components that
 * read `original_price_cents` from the backend row — those represent
 * actual planned price changes and are fine.
 *
 * Variant controls scale so the same component looks right in a small
 * rail card and a hero buy block. */

export function PriceWithAnchor({
  priceCents,
  variant = "card",
}: {
  priceCents: number | null | undefined;
  /* `slug` kept in props for backward compat with existing call sites
   * but no longer used now that the fake hash-based anchor is gone. */
  slug?: string;
  variant?: "card" | "hero" | "rail";
}) {
  /* SOP v9.0 §1.1 — never render `$NaN`. */
  if (
    priceCents == null ||
    !Number.isFinite(Number(priceCents)) ||
    Number(priceCents) <= 0
  ) {
    return null;
  }
  const cents = Number(priceCents);
  const sale = (cents / 100).toFixed(0);

  const sizes =
    variant === "hero"
      ? { sale: 48 }
      : variant === "rail"
        ? { sale: 16 }
        : { sale: 20 };

  return (
    <span
      className="font-mono"
      style={{
        fontSize: sizes.sale,
        color: "var(--color-fg)",
        letterSpacing: "-0.02em",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      ${sale}
    </span>
  );
}
